// Copyright (c) 2022-2023. Sendanor <info@sendanor.fi>. All rights reserved.

import { SmsTokenDTO } from "../../../../io/hyperify/core/auth/sms/types/SmsTokenDTO";
import { map } from "../../../../io/hyperify/core/functions/map";
import { isClientListDTO } from "../../../../io/hyperify/core/store/types/client/ClientListDTO";
import { isClientDTO, ClientDTO } from "../../../../io/hyperify/core/store/types/client/ClientDTO";
import { HttpService } from "../../../../io/hyperify/core/HttpService";
import { ReadonlyJsonAny, ReadonlyJsonObject } from "../../../../io/hyperify/core/Json";
import { NewInventoryItemDTO } from "../../../../io/hyperify/core/store/types/inventory/NewInventoryItemDTO";
import { StoreProductService } from "../../../../io/hyperify/core/StoreProductService";
import {
    API_URL,
    MY_CLIENT_API_URL,
    MY_CLIENT_LIST_API_URL,
    MY_INVENTORY_ITEM_API_UPDATE_URL,
    MY_INVENTORY_ITEM_API_URL,
    MY_INVENTORY_LIST_API_URL,
    MY_INVOICE_API_URL,
    MY_INVOICE_LIST_API_URL,
    MY_ORDER_API_URL,
    MY_ORDER_LIST_API_URL,
    MY_PAYMENT_API_URL,
    MY_PAYMENT_LIST_API_URL,
    MY_CLIENT_PERMISSION_LIST_API_URL,
    MY_PERMISSION_LIST_API_URL,
    MY_DOMAIN_SEARCH_URL,
    MY_INVOICE_CREATE_PAYMENT_TRANSACTION_API_URL,
    MY_TASK_LIST_API_URL, AGENT_VPS_INVENTORY_LIST_API_URL, AGENT_INVENTORY_ITEM_API_URL, AGENT_INVENTORY_ITEM_API_UPDATE_URL, AGENT_WEB_INVENTORY_LIST_API_URL, AGENT_SHELL_INVENTORY_LIST_API_URL, AGENT_INVENTORY_ITEM_API_CREATE_URL
} from "../constants/backend";
import { NewClientDTO } from "../../../../io/hyperify/core/store/types/client/NewClientDTO";
import { LogService } from "../../../../io/hyperify/core/LogService";
import { isErrorDTO } from "../../../../io/hyperify/core/types/ErrorDTO";
import { EmailTokenDTO } from "../../../../io/hyperify/core/auth/email/types/EmailTokenDTO";
import { SendanorApiHeader } from "../types/SendanorApiHeader";
import { AuthorizationUtils } from "../../../../io/hyperify/core/AuthorizationUtils";
import { NewOrderDTO } from "../../../../io/hyperify/core/store/types/order/NewOrderDTO";
import { explainOrderDTO, isOrderDTO, OrderDTO } from "../../../../io/hyperify/core/store/types/order/OrderDTO";
import { isInvoiceDTO, InvoiceDTO, explainInvoiceDTO } from "../../../../io/hyperify/core/store/types/invoice/InvoiceDTO";
import { isInvoiceListDTO } from "../../../../io/hyperify/core/store/types/invoice/InvoiceListDTO";
import { isInventoryItemListDTO } from "../../../../io/hyperify/core/store/types/inventory/InventoryItemListDTO";
import { isInventoryItemDTO, InventoryItemDTO, explainInventoryItemDTO } from "../../../../io/hyperify/core/store/types/inventory/InventoryItemDTO";
import { isPaymentDTO, PaymentDTO } from "../../../../io/hyperify/core/store/types/payment/PaymentDTO";
import { isPaymentListDTO } from "../../../../io/hyperify/core/store/types/payment/PaymentListDTO";
import { isOrderListDTO } from "../../../../io/hyperify/core/store/types/order/OrderListDTO";
import { PermissionList } from "../../../../io/hyperify/core/PermissionUtils";
import { isPermissionListDTO } from "../../../../io/hyperify/core/types/PermissionListDTO";
import { isSendanorApiPermission, SendanorApiPermission } from "../types/SendanorApiPermission";
import { DomainSearchDTO, explainDomainSearchDTO, isDomainSearchDTO } from "../../../../io/hyperify/core/store/types/domain/DomainSearchDTO";
import { LogLevel } from "../../../../io/hyperify/core/types/LogLevel";
import { PaytrailPaymentMethodGroup } from "../../../../io/hyperify/core/paytrail/types/PaytrailPaymentMethodGroup";
import { TaskDTO } from "../../../../io/hyperify/core/store/types/task/TaskDTO";
import { isTaskListDTO } from "../../../../io/hyperify/core/store/types/task/TaskListDTO";

const LOG = LogService.createLogger('SendanorClientHttpService');

/**
 * This is the client part of the API for sendanor.fi.
 *
 * See {@link EmailAuthHttpService} for login and authentication.
 */
export class SendanorClientHttpService {

    private static _apiUrl : string = API_URL;

    public static setApiUrl (url: string) : void {
        this._apiUrl = url;
    }

    public static setLogLevel (level: LogLevel) {
        LOG.setLogLevel(level);
    }

    /**
     * Returns customer's current client records
     */
    public static async searchDomain (
        searchString  : string
    ) : Promise<DomainSearchDTO> {
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_DOMAIN_SEARCH_URL(searchString)
        );
        if ( !isDomainSearchDTO(response) ) {
            throw new TypeError(`SendanorClientHttpService.searchDomain: Response was not DomainSearchDTO: ${explainDomainSearchDTO(response)}`);
        }
        return response;
    }

    /**
     * Returns customer's current client records
     *
     * @see System tested in ClientApiSystem.test.ts
     */
    public static async getMyClientList (
        token  : EmailTokenDTO | SmsTokenDTO
    ) : Promise<readonly ClientDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_CLIENT_LIST_API_URL(),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if ( isClientListDTO(response) ) {
            return response.payload;
        }
        return [];
    }

    /**
     * Returns tasks
     */
    public static async getMyTaskList (
        token  : EmailTokenDTO | SmsTokenDTO
    ) : Promise<readonly TaskDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_TASK_LIST_API_URL(),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if ( isTaskListDTO(response) ) {
            return response.payload;
        }
        return [];
    }

    /**
     * Returns customer's client record by id.
     *
     * @see System tested in ClientApiSystem.test.ts
     */
    public static async getMyClientById (
        id     : string,
        token  : EmailTokenDTO | SmsTokenDTO
    ) : Promise<ClientDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_CLIENT_API_URL(id),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        LOG.debug(`response = `, response);
        if ( isErrorDTO(response) ) {
            throw response;
        }
        if ( !isClientDTO(response) ) {
            throw new TypeError('Response was not SendanorClientDTO: ' + response);
        }
        return response;
    }

    /**
     * Create a new client record
     *
     * @see System tested in ClientApiSystem.test.ts
     */
    public static async addMyClient (
        item   : NewClientDTO,
        token  : EmailTokenDTO | SmsTokenDTO
    ) : Promise<ClientDTO> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.postJson(
            this._apiUrl + MY_CLIENT_LIST_API_URL(),
            item as ReadonlyJsonObject,
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if ( !isClientDTO(response) ) {
            LOG.debug(`response = `, response);
            throw new TypeError('Response was not SendanorClientDTO: ' + response);
        }
        return response;
    }


    /**
     * Create a new order
     */
    public static async sendMyOrder (
        clientId : string,
        item     : NewOrderDTO,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<OrderDTO> {
        LOG.debug(`sendMyOrder: `, clientId, item, token);
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.postJson(
            this._apiUrl + MY_ORDER_LIST_API_URL(clientId),
            item as unknown as ReadonlyJsonObject,
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if ( !isOrderDTO(response) ) {
            LOG.debug(`response = `, response);
            throw new TypeError('Response was not OrderDTO: ' + explainOrderDTO(response));
        }
        StoreProductService.refreshProducts();
        return response;
    }

    /**
     * Returns user's API permissions
     *
     * @see System tested in EmailAuthSystem.test.ts
     */
    public static async getMyPermissionList (
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<PermissionList<SendanorApiPermission>> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_PERMISSION_LIST_API_URL(),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isPermissionListDTO<SendanorApiPermission>(response, isSendanorApiPermission) ? response?.permissions : [];
    }

    /**
     * Returns user's API permissions for specific client
     *
     * @see System tested in ClientApiSystem.test.ts
     */
    public static async getMyClientPermissionList (
        clientId : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<PermissionList<SendanorApiPermission>> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_CLIENT_PERMISSION_LIST_API_URL(clientId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isPermissionListDTO<SendanorApiPermission>(response, isSendanorApiPermission) ? response?.permissions : [];
    }

    /**
     * Returns customer's order records
     */
    public static async getMyOrderList (
        clientId : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<readonly OrderDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_ORDER_LIST_API_URL(clientId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isOrderListDTO(response) ? response.payload : [];
    }

    /**
     * Create payment transaction through Paytrail API
     */
    public static async createPaymentTransaction (
        group     : PaytrailPaymentMethodGroup,
        clientId  : string,
        invoiceId : string,
        token     : EmailTokenDTO | SmsTokenDTO
    ) : Promise<InvoiceDTO> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.postJson(
            this._apiUrl + MY_INVOICE_CREATE_PAYMENT_TRANSACTION_API_URL(clientId, invoiceId),
            {
                group
            },
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if (!isInvoiceDTO(response)) {
            throw new TypeError(`Response was not invoice DTO: ${explainInvoiceDTO(response)}`);
        }
        return response;
    }

    /**
     * Returns customer's order record
     */
    public static async getMyOrderById (
        clientId : string,
        orderId  : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<OrderDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_ORDER_API_URL(clientId, orderId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if (!isOrderDTO(response)) {
            LOG.debug(`getMyOrder: response: `, response);
            throw new TypeError(`Response was not SendanorOrderDTO: ${response}`);
        }
        return response;
    }


    /**
     * Returns customer's current invoice records
     */
    public static async getMyInvoiceList (
        clientId : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<readonly InvoiceDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_INVOICE_LIST_API_URL(clientId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isInvoiceListDTO(response) ? response.payload : [];
    }

    /**
     * Returns customer's invoice record
     */
    public static async getMyInvoiceById (
        clientId : string,
        itemId   : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<InvoiceDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_INVOICE_API_URL(clientId, itemId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if (!isInvoiceDTO(response)) {
            LOG.debug(`getMyInvoice: response: `, response);
            throw new TypeError(`Response was not SendanorInvoiceDTO: ${response}`);
        }
        return response;
    }

    /**
     * Returns customer's current inventory records
     */
    public static async getMyInventoryList (
        clientId : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<readonly InventoryItemDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_INVENTORY_LIST_API_URL(clientId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isInventoryItemListDTO(response) ? response.payload : [];
    }

    /**
     * Returns agent's vps inventory records
     */
    public static async getAgentVpsInventoryList (
        token    : EmailTokenDTO | SmsTokenDTO,
        system  ?: string | undefined,
        name    ?: string | undefined,
    ) : Promise<readonly InventoryItemDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + AGENT_VPS_INVENTORY_LIST_API_URL(system, name),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isInventoryItemListDTO(response) ? response.payload : [];
    }

    /**
     * Returns agent's shell inventory records
     */
    public static async getAgentShellInventoryList (
        token     : EmailTokenDTO | SmsTokenDTO,
        hostname ?: string | undefined,
        username ?: string | undefined,
    ) : Promise<readonly InventoryItemDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + AGENT_SHELL_INVENTORY_LIST_API_URL(hostname, username),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isInventoryItemListDTO(response) ? response.payload : [];
    }

    /**
     * Returns agent's web inventory records
     */
    public static async getAgentWebInventoryList (
        token    : EmailTokenDTO | SmsTokenDTO,
        system  ?: string | undefined,
        name    ?: string | undefined,
    ) : Promise<readonly InventoryItemDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + AGENT_WEB_INVENTORY_LIST_API_URL(system, name),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isInventoryItemListDTO(response) ? response.payload : [];
    }

    /**
     * Returns agent's virtual server inventory record
     */
    public static async getAgentVpsInventoryItem (
        token    : EmailTokenDTO | SmsTokenDTO,
        name    ?: string | undefined,
        system  ?: string | undefined,
    ) : Promise<InventoryItemDTO> {
        const list = await this.getAgentVpsInventoryList(
            token,
            system,
            name,
        );
        if (list?.length === 0) {
            throw new Error(`No VPS found for ${name}@${system??'*'}`);
        }
        if (list?.length !== 1) {
            throw new Error(`Multiple items found: ${map(list, item => item.inventoryItemId).join(', ')}`);
        }
        const item = list[0];
        return await this.getAgentInventoryItem(token, item.inventoryItemId);
    }

    /**
     * Returns agent's shell inventory record
     */
    public static async getAgentShellInventoryItem (
        token     : EmailTokenDTO | SmsTokenDTO,
        username ?: string | undefined,
        hostname ?: string | undefined,
    ) : Promise<InventoryItemDTO> {
        const list = await this.getAgentShellInventoryList(
            token,
            hostname,
            username,
        );
        if (list?.length === 0) {
            throw new Error(`No shell found for ${username}@${hostname??'*'}`);
        }
        if (list?.length !== 1) {
            throw new Error(`Multiple items found: ${map(list, item => item.inventoryItemId).join(', ')}`);
        }
        const item = list[0];
        return await this.getAgentInventoryItem(token, item.inventoryItemId);
    }

    /**
     * Returns agent's web inventory record
     */
    public static async getAgentWebInventoryItem (
        token    : EmailTokenDTO | SmsTokenDTO,
        name    ?: string | undefined,
        system  ?: string | undefined,
    ) : Promise<InventoryItemDTO> {
        const list = await this.getAgentWebInventoryList(
            token,
            system,
            name,
        );
        if (list?.length === 0) {
            throw new Error(`No webhotel found for ${name}@${system??'*'}`);
        }
        if (list?.length !== 1) {
            throw new Error(`Multiple items found: ${map(list, item => item.inventoryItemId).join(', ')}`);
        }
        const item = list[0];
        return await this.getAgentInventoryItem(token, item.inventoryItemId);
    }

    /**
     * Returns agent's inventory record by id
     */
    public static async getAgentInventoryItem (
        token    : EmailTokenDTO | SmsTokenDTO,
        id       : string,
    ) : Promise<InventoryItemDTO> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + AGENT_INVENTORY_ITEM_API_URL(id),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if (isErrorDTO(response)) throw response;
        if (!isInventoryItemDTO(response)) throw new TypeError('Not InventoryItemDTO');
        return response;
    }

    /**
     * Updates inventory item
     */
    public static async updateAgentInventoryItemById (
        itemId   : string,
        token    : EmailTokenDTO | SmsTokenDTO,
        data     : InventoryItemDTO
    ) : Promise<InventoryItemDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.postJson(
            this._apiUrl + AGENT_INVENTORY_ITEM_API_UPDATE_URL(itemId),
            data,
            SendanorClientHttpService._createHeaders(sessionToken),
        );
        if (!isInventoryItemDTO(response)) {
            LOG.debug(`updateAgentInventoryItemById: response: `, response);
            throw new TypeError(`SendanorClientHttpService.updateAgentInventoryItemById: Response was not InventoryItemDTO: ${explainInventoryItemDTO(response)}`);
        }
        return response;
    }

    /**
     * Create inventory item
     */
    public static async createAgentInventoryItem (
        token    : EmailTokenDTO | SmsTokenDTO,
        data     : NewInventoryItemDTO
    ) : Promise<InventoryItemDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.postJson(
            this._apiUrl + AGENT_INVENTORY_ITEM_API_CREATE_URL(),
            data as any as ReadonlyJsonObject,
            SendanorClientHttpService._createHeaders(sessionToken),
        );
        if (!isInventoryItemDTO(response)) {
            LOG.debug(`createAgentInventoryItem: response: `, response);
            throw new TypeError(`SendanorClientHttpService.createAgentInventoryItem: Response was not InventoryItemDTO: ${explainInventoryItemDTO(response)}`);
        }
        return response;
    }

    /**
     * Returns customer's payment record
     */
    public static async getMyInventoryItemById (
        clientId : string,
        itemId   : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<InventoryItemDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_INVENTORY_ITEM_API_URL(clientId, itemId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if (!isInventoryItemDTO(response)) {
            LOG.debug(`getMyInventoryItemById: response: `, response);
            throw new TypeError(`SendanorClientHttpService.getMyInventoryItemById: Response was not InventoryItemDTO: ${explainInventoryItemDTO(response)}`);
        }
        return response;
    }

    /**
     * Updates inventory item
     */
    public static async updateMyInventoryItemById (
        clientId : string,
        itemId   : string,
        token    : EmailTokenDTO | SmsTokenDTO,
        data     : InventoryItemDTO
    ) : Promise<InventoryItemDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.postJson(
            this._apiUrl + MY_INVENTORY_ITEM_API_UPDATE_URL(clientId, itemId),
            data,
            SendanorClientHttpService._createHeaders(sessionToken),
        );
        if (!isInventoryItemDTO(response)) {
            LOG.debug(`updateMyInventoryItemById: response: `, response);
            throw new TypeError(`SendanorClientHttpService.updateMyInventoryItemById: Response was not InventoryItemDTO: ${explainInventoryItemDTO(response)}`);
        }
        return response;
    }

    /**
     * Returns customer's current recurring or open payment records
     */
    public static async getMyPaymentList (
        clientId : string,
        token    : EmailTokenDTO | SmsTokenDTO
    ) : Promise<readonly PaymentDTO[]> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_PAYMENT_LIST_API_URL(clientId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        return isPaymentListDTO(response) ? response.payload : [];
    }

    /**
     * Returns customer's payment record
     */
    public static async getMyPaymentById (
        clientId  : string,
        paymentId : string,
        token     : EmailTokenDTO | SmsTokenDTO
    ) : Promise<PaymentDTO | undefined> {
        const sessionToken = SendanorClientHttpService._getSessionToken(token);
        const response : ReadonlyJsonAny | undefined = await HttpService.getJson(
            this._apiUrl + MY_PAYMENT_API_URL(clientId, paymentId),
            SendanorClientHttpService._createHeaders(sessionToken)
        );
        if (!isPaymentDTO(response)) {
            LOG.debug(`getMyPayment: response: `, response);
            throw new TypeError(`Response was not SendanorPaymentDTO: ${response}`);
        }
        return response;
    }


    private static _getSessionToken (
        token: EmailTokenDTO | SmsTokenDTO
    ) : string {
        const sessionToken = token?.token;
        if (!sessionToken) throw new TypeError(`Token is required`);
        if (!token?.verified) throw new TypeError(`Token must be verified`);
        return sessionToken;
    }

    private static _createHeaders (
        sessionToken: string
    ) : {[key: string]: string} {
        return {
            [SendanorApiHeader.AUTHORIZATION]: AuthorizationUtils.createBearerHeader(sessionToken)
        };
    }

}
